import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Buttons, FlexContainer, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { showChat } from 'Services/chat';
import { showModalAction } from 'Store/modules/app/actions';
import useTranslation from 'Hooks/useTranslation';
import { useGetUserCustomerData } from 'Containers/Datagathering/views/DgTypeForm/hooks';
import ConfirmClabeBanner from 'Components/ConfirmClabeBanner';
import SaveClabeBanner from 'Components/SaveClabeBanner';
import { lottiesPaths } from 'Constants/lottiesPaths';
import { triggerModalForReview } from 'Utils/native';
import { PDF_MODAL } from 'Constants/modals';
import { FRONT_PLANS, plansNameMapping } from 'Constants/plans';
import { TaxScreenType } from 'Types/common';
import * as Styled from '../styles';
import ResultInfo from '../ResultInfo';
import { addDaysToDate } from '../utils';
import { RESOLUTION_DAYS_OFFSET } from './utils';

const Accepted = ({ tax: { taxYear, filingInfo, pdf } }: TaxScreenType): JSX.Element => {
  const { t } = useTranslation(locizeNS.DASHBOARD);
  const dispatch = useDispatch();
  const customerInfo = useGetUserCustomerData();
  const plan = useMemo(
    () =>
      customerInfo?.activePlan?.name
        ? plansNameMapping(customerInfo.activePlan.name)
        : FRONT_PLANS.BASIC,
    [customerInfo]
  );

  useEffect(() => {
    triggerModalForReview();
  }, []);
  const handleShowDraft = (): void => {
    dispatch(showModalAction({ options: { type: PDF_MODAL, pdf } }));
  };

  const isFlex = plan && plan === FRONT_PLANS.FLEX;

  const descriptionDate = addDaysToDate(filingInfo?.recent?.filedAt, 40);

  const resolutionDate = filingInfo?.recent?.taxAgencyResolvedAt
    ? Math.max(
        Math.ceil(
          (new Date(filingInfo.recent.taxAgencyResolvedAt).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24)
        ) + RESOLUTION_DAYS_OFFSET,
        0
      )
    : RESOLUTION_DAYS_OFFSET;
  return (
    <FlexContainer direction="column" fullHeight fullWidth gap="dimension_16">
      <SaveClabeBanner />
      <Styled.InfoContainer
        justify="center"
        direction="column"
        align="center"
        gap="dimension_08"
        fullHeight
      >
        <Text size="L_L_S" color="dark" type="headline">
          {taxYear}
        </Text>
        <Text type="paragraphs" size="L_L_M" color="dark" align="center">
          {t('Dashboard.Accepted.congratulations')}
        </Text>
        <Text type="paragraphs" size="L_L_M" color="dark" align="center">
          {t('Dashboard.Accepted.message')}
        </Text>
        <Styled.StyledLottie
          options={{ path: lottiesPaths.PAID, rendererSetting: { progressiveLoad: true } }}
        />
        {filingInfo && <ResultInfo filingInfo={filingInfo} />}
        <Text type="paragraphs" color="dark" size="M_L_S" align="center">
          {t(`Accepted.title`, {
            context: isFlex && 'isFlex',
            date: resolutionDate,
          })}
        </Text>
        <Text type="paragraphs" color="dark" size="S_S_S" align="center">
          {t(`Accepted.description`, {
            context: isFlex && 'isFlex',
            date: descriptionDate,
          })}
        </Text>
        <FlexContainer direction="column" align="center" gap="dimension_16">
          <Styled.ButtonsWrapper direction="row-reverse" gap="dimension_16">
            {pdf && (
              <Buttons.Classic
                size="m"
                mode="primary"
                onClick={handleShowDraft}
                label={t('Dashboard.Submitted.firstButton')}
              />
            )}
            <Buttons.Classic
              size="m"
              mode="light"
              label={t('Dashboard.Validation.button')}
              onClick={showChat}
            />
          </Styled.ButtonsWrapper>
          <ConfirmClabeBanner />
        </FlexContainer>
      </Styled.InfoContainer>
    </FlexContainer>
  );
};

export default Accepted;
